import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import { config } from "node-config-ts";
import { CaptureStepTypes } from "@enums/capture-step-types";
import { useQueryParameters } from "@hooks/use-query-parameters";
import { useInitializeApplication } from "@hooks/use-initialize-application";
import { useInitializeLanguage } from "@hooks/use-initialize-language";
import { useGoogleAnalytics } from "@hooks/use-google-analytics";
import { useScrollToTop } from "@hooks/use-scroll-to-top";
import { useDeviceDetector } from "@hooks/use-device-detector";
import { useInitializeAcuantSdk } from "@hooks/use-initialize-acuant-sdk";
import { useInitializeValidationResults } from "@hooks/use-initialize-validation-results";
import { useTmx } from "@hooks/use-tmx";
import { setError } from "@redux/actions/error-actions";
import { getQueryParametersState } from "@redux/selectors/query-parameters-selectors";
import { getVerificationConfigurationState } from "@redux/selectors/verification-configuration-selectors";
import { Logger } from "@utils/logger";
import { Spinner } from "../common/spinner/spinner";
import { ContentContainer } from "../content/content-container";

export const DcContainer = () => {
    const { APPLICATION_ENVIRONMENT, REACT_APP_GA_KEY } = config;

    const dispatch = useDispatch();

    useEffect(() => {
        Logger.info('**** [App] Launching the DCUI App ****');
    }, []);

    useQueryParameters();
    useInitializeApplication();
    useInitializeLanguage();
    useInitializeAcuantSdk();
    useInitializeValidationResults();
    useTmx();
    useGoogleAnalytics(APPLICATION_ENVIRONMENT, REACT_APP_GA_KEY);
    useScrollToTop();

    const deviceInfo = useDeviceDetector();
    const location = useLocation();
    const queryParametersState = useSelector(getQueryParametersState);
    const verificationConfigurationState = useSelector(getVerificationConfigurationState);

    if (!verificationConfigurationState.loadingFailed && (verificationConfigurationState.loading || verificationConfigurationState.config === null)) {
        Logger.info('[App] showing Loading spinner');
        return <Spinner />;
    }

    const pathName = location.pathname || window.location.pathname;
    let errorPath = "/error";
    if (pathName.includes("dcui")) {
        errorPath = "/dcui/error";
    }

    if (verificationConfigurationState.loadingFailed && pathName !== errorPath) {
        Logger.info("[App] return error redirect");
        return (
            <Navigate to={errorPath} replace />
        );
    }

    const steps = verificationConfigurationState.config?.stepConfig?.steps || [];
    const hasLivenessInSteps = steps.some(step => step.type === CaptureStepTypes.LIVENESS_CHECK);

    if (hasLivenessInSteps && deviceInfo?.browser?.name === "IE" && window.location.pathname !== errorPath) {
        dispatch(setError({
            name: "Incompatible Browser",
            asi: queryParametersState.token,
            errorCode: `${500}`,
            errorURI: "UI",
            httpStatus: "500",
            message: "Incompatible Browser"
        }));

        return <Navigate to={errorPath} replace />;
    }

    return <ContentContainer />;
};